// Vendored from the JETCAM DXF Viewer (src/render/linetypes.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type LtypeRecord } from "../parse/index.ts";

/**
 * Canvas dash arrays for linetypes a file's own LTYPE table does not define —
 * a hand-edited file whose entity carries a linetype name but no matching
 * table record. Values are in drawing units and assume a roughly
 * millimetre-scale part, matching this viewer's usual files; there is no
 * linetype-scale handling (`$LTSCALE`, group 48) in this pass, so these are
 * drawn as-is.
 */
const FALLBACK_PATTERNS: Readonly<Record<string, readonly number[]>> = {
  DASHED: [6, 3],
  DASHED2: [3, 1.5],
  DASHEDX2: [12, 6],
  DOT: [0, 3],
  DOT2: [0, 1.5],
  DOTX2: [0, 6],
  DASHDOT: [6, 3, 0, 3],
  DASHDOT2: [3, 1.5, 0, 1.5],
  DASHDOTX2: [12, 6, 0, 6],
  DIVIDE: [6, 3, 0, 3, 0, 3],
  CENTER: [12, 3, 3, 3],
  CENTRE: [12, 3, 3, 3],
  CENTER2: [8, 2, 2, 2],
  CENTERX2: [24, 6, 6, 6],
  HIDDEN: [3, 3],
  HIDDEN2: [1.5, 1.5],
  HIDDENX2: [6, 6],
  PHANTOM: [6, 1.5, 1.5, 1.5, 1.5, 1.5],
  BORDER: [6, 1.5, 1.5, 1.5],
};

/**
 * Resolve a linetype name to a Canvas `setLineDash` array.
 *
 * Prefers the file's own LTYPE table when it declares that name with actual
 * dash data (group 49: negative = gap, zero = dot, positive = dash — the
 * absolute value of each, in file order, is already a valid dash array; a
 * zero entry draws as a round dot given the renderer's round line cap).
 * Falls back to a built-in pattern for standard names when the file doesn't
 * define one. `[]` (solid) for CONTINUOUS, an unrecognised name, or a
 * declared-but-empty LTYPE record.
 */
export function resolveDashPattern(
  linetypeName: string,
  ltypes: ReadonlyMap<string, LtypeRecord>,
): readonly number[] {
  if (linetypeName === "CONTINUOUS") return [];

  const declared = ltypes.get(linetypeName);
  if (declared && declared.dashes.length > 0) return declared.dashes.map(Math.abs);

  return FALLBACK_PATTERNS[linetypeName.toUpperCase()] ?? [];
}
